import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { matchItem } from './navConfig';

/**
 * «Все разделы» — карта меню одним окном (аудит №3, U15).
 *
 * Все экраны, которые человеку можно открыть, сгруппированы так же, как в
 * меню, с подсказкой к каждому. Сверху — поиск (то же, что Ctrl/⌘+K).
 * Нужна, чтобы ни один экран не «потерялся» при свёрнутых группах и на
 * узком меню: всё видно сразу.
 */
export default function AllSections({ sections, onClose, initialQuery = '' }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const shown = useMemo(() => sections
    .map(sec => ({ ...sec, items: sec.items.filter(i => matchItem(i, query, sec.label)) }))
    .filter(sec => sec.items.length > 0), [sections, query]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      const first = shown[0] && shown[0].items[0];
      if (first) { e.preventDefault(); navigate(first.path); onClose(); }
    }
  };

  return (
    <div className="all-sections-overlay" onClick={onClose}>
      <div className="all-sections" role="dialog" aria-label="Все разделы" onClick={e => e.stopPropagation()}>
        <div className="all-sections__head">
          <h2 className="all-sections__title">Все разделы</h2>
          <button type="button" className="all-sections__close" aria-label="Закрыть" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="all-sections__search">
          <Search size={16} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Найти раздел: название или что в нём делают"
            aria-label="Поиск раздела"
          />
        </div>
        <div className="all-sections__body">
          {shown.length === 0 && (
            <div className="all-sections__empty">
              Раздела «{query}» нет среди доступных вам. Проверьте написание или
              попросите руководителя открыть доступ.
            </div>
          )}
          {shown.map(sec => (
            <section key={sec.label || 'main'} className="all-sections__group">
              {sec.label && <h3 className="all-sections__group-title">{sec.label}</h3>}
              {sec.items.map(item => {
                const Icon = item.icon;
                return (
                  <Link key={item.path} to={item.path} className="all-sections__item" onClick={onClose}>
                    <Icon size={17} className="all-sections__icon" />
                    <span className="all-sections__text">
                      <span className="all-sections__label">{item.label}</span>
                      {item.hint && <span className="all-sections__hint">{item.hint}</span>}
                    </span>
                  </Link>
                );
              })}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
